import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Loading } from '@/components/ui/Loading'
import { EntityHtml } from '@/components/ui/EntityHtml'
import { useGlobalEntitySearch, ENTITY_META } from '@/lib/api/rpgApi'
import { useT, useLocalizedHref } from '@/i18n'
import { useOwnedBookIds } from '@/lib/entitlements/useEntitlements'
import { isEntityLockedForOwner } from '@/lib/entitlements/contentAccess'
import { unlockHref } from './LockedContent'
import { Lock } from 'lucide-react'

/** Site-wide entity search (command-palette style). Fans across entity types. */
export function GlobalSearch({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT()
  const localized = useLocalizedHref()
  const [q, setQ] = useState('')
  const navigate = useNavigate()
  const { data, isFetching } = useGlobalEntitySearch(q)
  const ownedBookIds = useOwnedBookIds()

  useEffect(() => {
    if (!open) setQ('')
  }, [open])

  return (
    <Modal open={open} onClose={onClose} title={t('encyclopedia.globalSearch.title')}>
      <div className="flex items-center gap-2 border-b border-blood-900/60 pb-3">
        <Search size={18} className="text-bone-dim" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('encyclopedia.globalSearch.placeholder')}
          className="w-full bg-transparent font-body text-bone outline-none placeholder:text-bone-dim"
          aria-label={t('encyclopedia.globalSearch.queryAria')}
        />
      </div>

      <div className="mt-4 max-h-[55vh] space-y-5 overflow-y-auto">
        {isFetching && <Loading label={t('encyclopedia.globalSearch.loading')} />}
        {!isFetching && q.length >= 2 && (data?.length ?? 0) === 0 && (
          <p className="py-6 text-center font-body text-sm text-bone-dim">{t('encyclopedia.globalSearch.empty', { q })}</p>
        )}
        {data?.map((group) => (
          <div key={group.type}>
            <p className="title-gothic mb-2 text-xs text-gold">{ENTITY_META[group.type]?.plural ?? group.type}</p>
            <ul className="space-y-1">
              {group.items.map((item) => {
                const locked = isEntityLockedForOwner(item, ownedBookIds)
                return (
                  <li key={String(item.id)}>
                    <button
                      onClick={() => {
                        navigate(
                          localized(
                            locked
                              ? unlockHref({ setting: item.setting, provenance: item.provenance }, `${group.type}/${item.id}`)
                              : `/encyclopedia/${group.type}/${item.id}`,
                          ),
                        )
                        onClose()
                      }}
                      className="flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left hover:bg-blood-900/30"
                    >
                      <EntityHtml html={item.html} className="text-sm text-bone" />
                      {locked && <Lock size={13} className="shrink-0 text-gold" aria-label={t('encyclopedia.lockAria')} />}
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>
    </Modal>
  )
}
